"use client";

import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import {
  Trophy,
  CalendarDays,
  Users,
  Layers,
} from "lucide-react";

// Smooth ease-out animated counter component
function CounterNumber({ target, duration = 2.2, start = false }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    let startTime = null;
    let animationFrame = null;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);

      // Smooth ease-out cubic
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easedProgress * target));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [target, duration, start]);

  return <span>{count}</span>;
}

const statsData = [
  {
    number: 11,
    label: "Awards",
    icon: Trophy,
    color: "#eab308", // gold/amber
    badge: "Excellence",
  },
  {
    number: 6,
    label: "Years",
    icon: CalendarDays,
    color: "#38bdf8", // sky
    badge: "Since 2020",
  },
  {
    number: 220,
    label: "Members",
    icon: Users,
    color: "#7dd3fc", // light blue
    badge: "Stronger Together",
  },
  {
    number: 7,
    label: "Subunits",
    icon: Layers,
    color: "#2dd4bf", // teal
    badge: "Dynamic Chapters",
  },
];

export default function AboutTeamDivider() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  return (
    <section
      ref={ref}
      className="relative py-16 md:py-20 overflow-hidden flex items-center justify-center"
    >
      {/* BACKGROUND IMAGE */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "url('/bg.webp')",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundAttachment: "fixed",
        }}
      />

      {/* BLUE GRADIENT OVERLAY */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "linear-gradient(135deg, rgba(0, 102, 153, 0.92) 0%, rgba(0, 34, 51, 0.96) 100%)",
        }}
      />

      {/* SUBTLE TEXTURE & LIGHT ACCENT */}
      <div
        className="absolute inset-0 opacity-[0.15] mix-blend-overlay pointer-events-none z-0"
        style={{
          backgroundImage:
            "url('https://grainy-gradients.vercel.app/noise.svg')",
        }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#0099cc]/20 rounded-full blur-3xl pointer-events-none z-0" />

      {/* MAIN CONTAINER */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 flex flex-col items-center gap-8">
        
        {/* TITLE */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center space-y-2"
        >
          

          <h2 className="text-white text-3xl sm:text-4xl md:text-5xl font-extralight tracking-tight flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
            <span className="font-bold tracking-wide">IEEE ISIMS SB</span>
            <span className="hidden sm:inline text-white/40 font-light">—</span>
            <span className="font-serif italic font-medium text-[#7dd3fc]">
              In Numbers
            </span>
          </h2>
        </motion.div>

        {/* 4 STATS SQUARES WITH COOL MOTION */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 w-full">
          {statsData.map((stat, index) => {
            const Icon = stat.icon;

            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 35 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  duration: 0.6,
                  delay: 0.15 + index * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{
                  y: -6,
                  scale: 1.02,
                  transition: { duration: 0.25, ease: "easeOut" },
                }}
                className="group relative cursor-pointer"
              >
                {/* CARD CONTAINER */}
                <div
                  className="
                    relative
                    rounded-none
                    p-5 sm:p-6
                    h-full
                    flex flex-col justify-between
                    bg-[#002233]/70 backdrop-blur-md
                    border border-white/15
                    group-hover:border-[#7dd3fc]/80
                    group-hover:bg-[#002b40]/80
                    shadow-xl
                    transition-all duration-300
                  "
                >
                  {/* Decorative L-corner brackets matching the theme */}
                  <span className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#7dd3fc]/70 group-hover:border-[#7dd3fc] transition-colors" />
                  <span className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#7dd3fc]/70 group-hover:border-[#7dd3fc] transition-colors" />

                  {/* Top Bar: Icon & Micro-badge */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2.5 bg-white/10 border border-white/20 group-hover:bg-[#006699]/40 group-hover:border-[#7dd3fc]/60 transition-all duration-300">
                      <Icon
                        className="w-5 h-5 sm:w-6 sm:h-6 text-white group-hover:scale-110 group-hover:-rotate-6 transition-transform duration-300"
                        style={{ color: stat.color }}
                      />
                    </div>
                    <span className="text-[10px] tracking-wider uppercase font-semibold text-white/50 group-hover:text-[#7dd3fc] transition-colors">
                      {stat.badge}
                    </span>
                  </div>

                  {/* Stat Number with Animated Counter */}
                  <div className="my-2">
                    <div className="text-4xl sm:text-5xl font-black text-white tracking-tight flex items-baseline gap-0.5">
                      <CounterNumber
                        target={stat.number}
                        duration={2.2}
                        start={isInView}
                      />
                      <span className="text-[#7dd3fc] font-light text-3xl sm:text-4xl">
                        +
                      </span>
                    </div>

                    <p className="text-xs sm:text-sm uppercase tracking-wider font-bold text-white/90 mt-1">
                      {stat.label}
                    </p>
                  </div>

                  <p className="text-xs sm:text-sm text-white/60 font-medium mt-3 border-t border-white/10 pt-2.5 group-hover:text-white/80 transition-colors">
                    {stat.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* FOOTER NOTE */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 1, duration: 0.6 }}
          className="text-white/40 text-[11px] tracking-widest uppercase text-center font-medium mt-2"
        >
          THE IEEE Higher Institute of Computer Science & Multimedia of Sfax SB
        </motion.p>
      </div>
    </section>
  );
}
